/**
 * Inventory Management API
 * Handles all inventory-related API calls for Claw9
 */

import { BASE_URL, fetchWithAuth } from './client';

/**
 * Helper to parse response and handle errors
 */
const handleResponse = async (response, errorMessage = 'Request failed') => {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || errorData.message || errorMessage);
    }
    return response.json();
};

// ============ Products ============

/**
 * Fetch all products with optional filters
 */
export const fetchProducts = async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.category) params.append('category', filters.category);
    if (filters.stockStatus) params.append('stockStatus', filters.stockStatus);
    if (filters.minPrice) params.append('minPrice', filters.minPrice);
    if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);

    const queryString = params.toString();
    const url = `${BASE_URL}/api/claw9/inventory/products${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);
    return handleResponse(response, 'Failed to fetch products');
};

/**
 * Fetch a single product by ID
 */
export const fetchProductById = async (productId) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/products/${productId}`);
    return handleResponse(response, 'Failed to fetch product');
};

/**
 * Create a new product
 */
export const createProduct = async (productData) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productData),
    });
    return handleResponse(response, 'Failed to create product');
};

/**
 * Update an existing product
 */
export const updateProduct = async (productId, productData) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/products/${productId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productData),
    });
    return handleResponse(response, 'Failed to update product');
};

/**
 * Delete a product
 */
export const deleteProduct = async (productId) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/products/${productId}`, {
        method: 'DELETE',
    });
    return handleResponse(response, 'Failed to delete product');
};

/**
 * Bulk delete products
 */
export const bulkDeleteProducts = async (productIds) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/products/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds }),
    });
    return handleResponse(response, 'Failed to delete products');
};

/**
 * Bulk update products (category, status, etc.)
 */
export const bulkUpdateProducts = async (productIds, updates) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/products/bulk-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds, updates }),
    });
    return handleResponse(response, 'Failed to update products');
};

// ============ Stock Management ============

/**
 * Adjust stock for a product
 */
export const adjustStock = async (productId, adjustment) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/products/${productId}/stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(adjustment),
    });
    return handleResponse(response, 'Failed to adjust stock');
};

/**
 * Get stock history for a product
 */
export const getStockHistory = async (productId, options = {}) => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);

    const queryString = params.toString();
    const url = `${BASE_URL}/api/claw9/inventory/products/${productId}/stock-history${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);
    return handleResponse(response, 'Failed to fetch stock history');
};

/**
 * Get low stock alerts
 */
export const getLowStockAlerts = async () => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/alerts/low-stock`);
    return handleResponse(response, 'Failed to fetch low stock alerts');
};

// ============ Categories ============

/**
 * Fetch all categories
 */
export const fetchCategories = async () => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/categories`);
    return handleResponse(response, 'Failed to fetch categories');
};

/**
 * Create a new category
 */
export const createCategory = async (categoryData) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryData),
    });
    return handleResponse(response, 'Failed to create category');
};

/**
 * Update a category
 */
export const updateCategory = async (categoryId, categoryData) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/categories/${categoryId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryData),
    });
    return handleResponse(response, 'Failed to update category');
};

/**
 * Delete a category
 */
export const deleteCategory = async (categoryId) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/categories/${categoryId}`, {
        method: 'DELETE',
    });
    return handleResponse(response, 'Failed to delete category');
};

// ============ Statistics ============

/**
 * Get inventory statistics
 */
export const getInventoryStats = async () => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/stats`);
    return handleResponse(response, 'Failed to fetch inventory stats');
};

/**
 * Get inventory value report
 */
export const getInventoryValueReport = async () => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/reports/value`);
    return handleResponse(response, 'Failed to fetch inventory value report');
};

/**
 * Get stock movement report
 */
export const getStockMovementReport = async (dateRange) => {
    const params = new URLSearchParams();
    if (dateRange.startDate) params.append('startDate', dateRange.startDate);
    if (dateRange.endDate) params.append('endDate', dateRange.endDate);

    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/reports/movement?${params.toString()}`);
    return handleResponse(response, 'Failed to fetch stock movement report');
};

// ============ Import/Export ============

/**
 * Import products from CSV
 */
export const importProducts = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/import`, {
        method: 'POST',
        body: formData,
    });
    return handleResponse(response, 'Failed to import products');
};

/**
 * Export products to CSV
 */
export const exportProducts = async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.stockStatus) params.append('stockStatus', filters.stockStatus);

    const queryString = params.toString();
    const url = `${BASE_URL}/api/claw9/inventory/export${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);
    // Export returns a blob, not JSON
    if (!response.ok) {
        throw new Error('Failed to export products');
    }
    return response;
};

/**
 * Upload product image
 */
export const uploadProductImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/upload-image`, {
        method: 'POST',
        body: formData,
    });
    return handleResponse(response, 'Failed to upload image');
};

// ============ Suppliers ============

/**
 * Fetch all suppliers
 */
export const fetchSuppliers = async () => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/suppliers`);
    return handleResponse(response, 'Failed to fetch suppliers');
};

/**
 * Create a new supplier
 */
export const createSupplier = async (supplierData) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/suppliers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(supplierData),
    });
    return handleResponse(response, 'Failed to create supplier');
};

/**
 * Update a supplier
 */
export const updateSupplier = async (supplierId, supplierData) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/suppliers/${supplierId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(supplierData),
    });
    return handleResponse(response, 'Failed to update supplier');
};

/**
 * Delete a supplier
 */
export const deleteSupplier = async (supplierId) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/inventory/suppliers/${supplierId}`, {
        method: 'DELETE',
    });
    return handleResponse(response, 'Failed to delete supplier');
};
